import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import ButtonItem from '@atlaskit/menu/button-item';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Avatar from '@atlaskit/avatar/avatar';
import { token } from '@atlaskit/tokens';

import { DualAvatarWithIdentityCards } from '@confluence/agent-user-dual-avatar/entry-points/AgentUserDualAvatar.ts';
import { fg } from '@confluence/feature-gating';

import { LastActive } from '../shared/LastActive.tsx';

import { AgentPresenceAvatar } from './AgentPresenceAvatar.tsx';
import type { AgentWithPresence } from './AgentPresenceTypes.ts';
import { isAgentPresenceInactive } from './agentPresenceStatus.ts';

const i18n = defineMessages({
	editing: {
		id: 'team-presence.agent-group-menu.editing',
		defaultMessage: 'Editing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is editing the page.',
	},
	editingWithActor: {
		id: 'team-presence.agent-group-menu.editing-with-actor',
		defaultMessage: 'with {actorName} • Editing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is editing on behalf of a user.',
	},
	viewing: {
		id: 'team-presence.agent-group-menu.viewing',
		defaultMessage: 'Viewing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is viewing the page.',
	},
	viewingWithActor: {
		id: 'team-presence.agent-group-menu.viewing-with-actor',
		defaultMessage: 'with {actorName} • Viewing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is viewing on behalf of a user.',
	},
});

export type AgentPresenceMenuItemProps = {
	actingUserAvatarUrl?: string;
	actingUserName?: string;
	agent: AgentWithPresence;
};

export const AgentPresenceMenuItem = ({
	actingUserAvatarUrl,
	actingUserName,
	agent,
}: AgentPresenceMenuItemProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleAgentClick = useCallback(
		(agentId: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'aiAgentPresenceGroupOverflowMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'aiAgentPresenceGroupOverflowMenuItem',
					product: 'confluence',
					attributes: {
						agentId,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const getPresenceDescription = () => {
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		if (!fg('platform_move_presence_agents')) {
			return undefined;
		}

		const activityDescription =
			agent.presenceActivity === 'editor' ? (
				actingUserName ? (
					<FormattedMessage {...i18n.editingWithActor} values={{ actorName: actingUserName }} />
				) : (
					<FormattedMessage {...i18n.editing} />
				)
			) : actingUserName ? (
				<FormattedMessage {...i18n.viewingWithActor} values={{ actorName: actingUserName }} />
			) : (
				<FormattedMessage {...i18n.viewing} />
			);
		const lastActive = agent.lastActive;
		const shouldShowLastActive = isAgentPresenceInactive(agent) && lastActive !== undefined;

		return (
			<>
				{activityDescription}
				{shouldShowLastActive && (
					<>
						<span aria-hidden="true">{' • '}</span>
						<LastActive timestamp={lastActive} />
					</>
				)}
			</>
		);
	};

	return (
		<ButtonItem
			onClick={() => handleAgentClick(agent.id)}
			iconBefore={
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				fg('platform_move_presence_agents') ? (
					actingUserName ? (
						<DualAvatarWithIdentityCards
							agentName={agent.name}
							isOffsetInlineStart={false}
							stack="default"
							agentAvatar={<AgentPresenceAvatar agent={agent} size="small" showBorder />}
							userName={actingUserName}
							userBorderColor={token('elevation.surface.overlay')}
							userAvatar={
								<Avatar
									src={actingUserAvatarUrl}
									name={actingUserName}
									size="xxsmall"
									borderColor="transparent"
									label=""
								/>
							}
						/>
					) : (
						<AgentPresenceAvatar agent={agent} size="small" showBorder />
					)
				) : (
					<AgentPresenceAvatar agent={agent} />
				)
			}
			description={getPresenceDescription()}
		>
			{agent.name}
		</ButtonItem>
	);
};
